//done
//@ts-check
import * as config from "../constants.js";
import { addNotification } from './notifications.js'
import { canEquipArtifact, getArtifactSkillRequirementMessage, canEquipTechniqueGear, getTechniqueGearRequirementMessage } from './character.js'
import { canCraftRecipe, getRecipeById, calculateBulkCraftingCost, getCraftingTree } from './crafting.js'
import { isArtifactUnlocked, duplicateCraftsAllowed, ownsTechGear, ownsArtifact } from './command-core.js'
import { updateUI } from "./game.js"
import { gameState, saveGameState } from "./gameState.js"
import { blackoutModifier } from './blackout-core.js';
import { materialValue } from './materialValue-core.js';
import { refreshAlpineInventory } from "./inventoryManager.js";
import { findArtifact, findItem, getAllArtifacts, getAllItems, findItemsById, getEffect, getSkillDisplayName, getTechniqueSkill, getTechniqueName, getTechniqueIcon} from "./util.js";
import { getConsumablePurchasePrice, recordEnergyPurchase } from "./raidUtil.js";
import { debug, isDebug, CH } from './debugLog.js';

// Initialize inventory with starting materials
export function initializeInventory() {
    if (!gameState.character) return;

    // Not gated: this path DISCARDS the character's whole inventory. It should
    // be loud if it ever fires unexpectedly.
    console.warn(`initializeInventory: clearing inventory for ${gameState.character.name}`);
    gameState.character.inventory = {};

    // Refresh Alpine store if available
    if (refreshAlpineInventory) {
        refreshAlpineInventory();
    }
}

export function getItemType(itemId) {
    // Check if it's a crafted artifact.
    //
    // ⚠ THE OPTIONAL CHAIN IS LOAD-BEARING: there is no character between
    // characters. Promotion and founding both legitimately set
    // `gameState.character = null`, and this helper is shared by the LAYER
    // pools — Command's vault, the Syndicate's dock and consignment — which
    // outlive every character by design (`inventoryManager.js` itemRows).
    //
    // Unguarded, this threw inside `refreshAlpineInventory` during
    // `loadGameState`, which aborted BOOT: a black page, and a save that failed
    // the same way on every reload afterwards. Both of the flows that reach it
    // are ordinary — promote your last character, or found a Syndicate — so the
    // account was bricked at exactly the moment a player would put the game
    // down. Found by the qa-player reload checks in §17/§35, 2026-08-05.
    if (gameState.character?.artifacts?.some(artifact => artifact.id === itemId)) {
        return 'artifact';
    }

    // Check in itemTable for the item's type
    const item = findItem('id', itemId);
    if (item) {
        return item.type;
    }

    // Check if it's a blueprint
    if (itemId.includes('-blueprint')) {
        return 'blueprint';
    }

    return 'material'; // Default type
}

export function getFactionIcon(factionKey) {
    return config.factionSkills[factionKey]?.icon || '';
}

export function getFactionName(factionKey) {
    return config.factionSkills[factionKey]?.name || '';
}

export function getTierColor(tier) {
    // Use itemRarities color scheme
    if (tier == 'currency') return '#ffd700';
    const rarityColor = config.itemRarities[tier]?.color;
    return rarityColor || '#9ca3af'; // Default to common
}

export function getTierSortOrder(tier) {
    // Define sorting order for tiers (higher number = higher priority)
    const tierOrder = {
        'unique': 6,
        'legendary': 5,
        'epic': 4,
        'rare': 3,
        'common': 2,
        'currency': 1
    };

    return tierOrder[tier] || 0;
}

export function getItemTypeColor(type) {
    // Return color schemes for different item categories
    const itemTypeColors = {
        'material': {
            header: 'from-stone-600 to-stone-700',
            button: 'from-stone-600 to-stone-700 hover:from-stone-700 hover:to-stone-800'
        },
        'consumable': {
            header: 'from-green-600 to-green-700',
            button: 'from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
        },
        'blueprint': {
            header: 'from-blue-600 to-blue-700',
            button: 'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
        },
        'relic': {
            header: 'from-purple-600 to-purple-700',
            button: 'from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800'
        },
        'storyRelic': {
            header: 'from-yellow-600 to-yellow-700',
            button: 'from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800'
        }
    };

    return itemTypeColors[type] || {
        header: 'from-gray-600 to-gray-700',
        button: 'from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
    };
}

export function getTierHeaderColor(tier) {
    // Return header color schemes for different item tiers/rarities
    const tierHeaderColors = {
        'common': 'from-gray-500 to-gray-600',
        'uncommon': 'from-green-500 to-green-600',
        'rare': 'from-blue-600 to-blue-700',
        'epic': 'from-purple-600 to-purple-700',
        'legendary': 'from-yellow-600 to-yellow-700',
        'unique': 'from-orange-600 to-orange-700',
        'currency': 'from-yellow-600 to-yellow-700'
    };

    return tierHeaderColors[tier] || 'from-gray-600 to-gray-700';
}

export function sortInventoryItems(items) {
    return items.sort((a, b) => {
        const aData = findItem('name', a.name);
        const bData = findItem('name', b.name);

        const aRarity = aData?.rarity || 'common';
        const bRarity = bData?.rarity || 'common';

        // First sort by tier priority (higher tier first)
        const tierDiff = getTierSortOrder(bRarity) - getTierSortOrder(aRarity);
        if (tierDiff !== 0) {
            return tierDiff;
        }

        // Then sort alphabetically by name
        return a.name.localeCompare(b.name);
    });
}

export function switchInventoryTab(categoryKey) {
    // Update tab buttons
    document.querySelectorAll('.inventory-tab-btn').forEach(btn => {
        btn.classList.remove('active');
        if (btn.dataset.category === categoryKey) {
            btn.classList.add('active');
        }
    });

    // Update content panels
    document.querySelectorAll('.inventory-category').forEach(panel => {
        panel.classList.remove('active');
        if (panel.dataset.category === categoryKey) {
            panel.classList.add('active');
        }
    });
}

export function useConsumable(itemId) {
    // Check if current character exists and is on marketplace
    if (!gameState.character) {
        addNotification('No active character!', 'error');
        return;
    }

    // //@ts-ignore - character is checked above
    const char = gameState.character;

    if (char.isMarketplace) {
        addNotification('Cannot use items with a character that is contracted out on the marketplace', 'error');
        return;
    }

    if (!char.inventory[itemId] || char.inventory[itemId] <= 0) {
        addNotification('You don\'t have any of this item!', 'error');
        return;
    }

    // Get consumable data
    const itemData = findItem('id', itemId);
    if (!itemData || itemData.type !== 'consumable') {
        addNotification('This item cannot be used!', 'error');
        return;
    }

    // Classify by the item's restore fields; technique consumables (epic,
    // effects-based) have neither and are deployed automatically in raids
    const isEnergyConsumable = !!itemData.restoresEnergy;
    const isHpConsumable = !!itemData.restoresHp;
    if (!isEnergyConsumable && !isHpConsumable) {
        addNotification('Technique consumables rig automatically on their technique\'s first success during a raid', 'info');
        return;
    }

    // Check raid state
    const isRaidActive = gameState.currentRaid && (gameState.currentRaid.raidInProgress || gameState.currentRaid.currentRaidGrid);

    // RESTRICTION 1: Energy consumables can ONLY be used before raid starts
    if (isEnergyConsumable) {
        if (isRaidActive) {
            addNotification('Energy consumables can only be used before starting a raid!', 'error');
            return;
        }
    }

    // RESTRICTION 2: HP consumables can be used before raid OR at campsite during raid
    if (isHpConsumable && gameState.currentRaid?.raidInProgress && !isAtCampsite()) {
        addNotification('HP consumables can only be used at a campsite during a raid!', 'error');
        return;
    }

    let used = false;
    let healAmount = 0;
    let energyAmount = 0;

    // Apply consumable effects based on type
    if (isHpConsumable) {
        if (char.currentHp < char.maxHp) {
            // Use specific value from item definition, or fallback to default
            healAmount = itemData.restoresHp || 35;
            char.currentHp = Math.min(char.maxHp, char.currentHp + healAmount);
            addNotification(`Used ${itemData.name}! Restored ${healAmount} HP.`, 'success');
            used = true;
        } else {
            addNotification('Your health is already full!', 'info');
        }
    } else if (isEnergyConsumable) {
        if (gameState.player && gameState.player.energy < gameState.player.maxEnergy) {
            // Use specific value from item definition, or fallback to default
            energyAmount = itemData.restoresEnergy || 30;
            gameState.player.energy = Math.min(gameState.player.maxEnergy, gameState.player.energy + energyAmount);
            addNotification(`Used ${itemData.name}! Restored ${energyAmount} energy.`, 'success');
            used = true;
        } else {
            addNotification('Your energy is already full!', 'info');
        }
    }

    if (used) {
        char.inventory[itemId]--;
        if (char.inventory[itemId] <= 0) {
            delete char.inventory[itemId];
        }

        // Update Alpine store if available
        if (refreshAlpineInventory) {
            refreshAlpineInventory();
        }

        updateUI();
        saveGameState();
    }
}

export function sellItem(itemId) {
    if (!gameState.character.inventory[itemId] || gameState.character.inventory[itemId] <= 0) {
        addNotification('You don\'t have any of this item to sell!', 'error');
        return;
    }

    // Get item data to determine sell price
    const itemData = findItem('id', itemId);
    if (!itemData) {
        addNotification('This item cannot be sold!', 'error');
        return;
    }

    // Get sell price from itemTable
    let sellPrice = 0;
    if (itemData.credits) {
        sellPrice = itemData.credits;
    } else {
        addNotification(`Cannot sell ${itemData.name} for credits`, 'error');
        return;
    }

    // Sell the quantity chosen via the +/- controls (defaults to 1)
    const qtyInput = document.getElementById(`sell-qty-${itemId}`);
    const quantity = Math.min(parseInt(qtyInput?.value) || 1, gameState.character.inventory[itemId]);

    gameState.character.inventory[itemId] -= quantity;
    if (gameState.character.inventory[itemId] <= 0) {
        delete gameState.character.inventory[itemId];
    }

    // Add credits to player
    gameState.player.credits += sellPrice * quantity;

    // Update Alpine store if available
    refreshAlpineInventory();

    // Show notification
    addNotification(`Sold ${quantity}x ${itemData.name} for ${sellPrice * quantity} credits`, 'success');

    // Update UI and save
    updateUI();
    saveGameState();
}

export function purchaseConsumable(itemId) {
    if (!gameState.character) {
        addNotification('No active character!', 'error');
        return;
    }

    if (gameState.character.isMarketplace) {
        addNotification('Cannot purchase items with a character that is contracted out on the marketplace', 'error');
        return;
    }

    // Get item data to determine purchase price
    const itemData = findItem('id', itemId);
    if (!itemData || itemData.type !== 'consumable') {
        addNotification('This item cannot be purchased!', 'error');
        return;
    }

    // Check if item has a credit cost
    if (!itemData.credits) {
        addNotification(`Cannot purchase ${itemData.name} for credits`, 'error');
        return;
    }

    // Energy consumables escalate with today's purchase count (credit sink)
    const purchasePrice = getConsumablePurchasePrice(itemData, gameState.player);

    // Check if player has enough credits
    if (!gameState.player || gameState.player.credits < purchasePrice) {
        addNotification(`Not enough credits! Need ${purchasePrice}, have ${gameState.player?.credits || 0}`, 'error');
        return;
    }

    // Deduct credits from player
    gameState.player.credits -= purchasePrice;
    if (itemData.restoresEnergy) {
        recordEnergyPurchase(gameState.player);
    }

    // Add item to inventory
    if (!gameState.character.inventory[itemId]) {
        gameState.character.inventory[itemId] = 0;
    }
    gameState.character.inventory[itemId]++;

    // Update Alpine store if available
    refreshAlpineInventory();

    // Re-render the purchase grid if the modal is open: energy prices escalate
    // per purchase, so stale BUY labels would understate the next charge
    const purchaseModal = document.getElementById('purchase-consumables-modal');
    if (purchaseModal && !purchaseModal.classList.contains('hidden')) {
        renderPurchaseModalGrid();
    }

    // Show notification
    addNotification(`Purchased ${itemData.name} for ${purchasePrice} credits`, 'success');

    // Update UI and save
    updateUI();
    saveGameState();
}

// Create inventory card HTML
// options: { collapsed: initial collapse state, progress: 0..1 override for
// the collapsed progress bar (crafting mode computes it from the recipe),
// hideActions: suppress SELL/USE/CRAFT buttons (display-only cards, e.g. the
// in-raid loot tab where items aren't in banked inventory yet);
// inModal: card is rendered inside the crafting modal, so the "CRAFT
// COMPONENTS" jump-to-tree button is replaced by the disabled MISSING
// MATERIALS state (the jump would just reopen the same modal) }
export function createInventoryCard(item, mode = 'inventory', recipe = null, preComputedMaterials = null, options = {}) {
    const isArtifact = item.type === 'artifact';
    const isSkillGear = item.type === 'techGear' || (item.type === 'consumable' && item.rarity === 'epic');
    const isConsumable = item.type === 'consumable';
    const itemData = mode == 'inventory' ? (isArtifact ? findArtifact('id', item.id) : findItem('id', item.id)) : item;
    debug(CH.ui, "creating card for item: ", item, mode, itemData);
    if (itemData) {
        const isCrafting = mode === 'crafting';
        const isPurchase = mode === 'purchase';

        // Determine card styling based on rarity in crafting mode
        const rarity = itemData.rarity || 'common';
        const rarityConfig = isCrafting ? config.itemRarities[rarity] : null;

        let cardBg, cardBorder, headerBg, headerBorder, headerText, categoryText;

        if (isCrafting && rarityConfig) {
            // Crafting mode with rarity colors
            const hexToTailwind = (hex) => `[${hex}]`;
            const color = rarityConfig.color;
            cardBg = `from-${hexToTailwind(color)}/10 to-${hexToTailwind(color)}/20`;
            cardBorder = `border-${hexToTailwind(color)}/30`;
            headerBg = `from-${hexToTailwind(color)}/50 to-${hexToTailwind(color)}/30`;
            headerBorder = `border-${hexToTailwind(color)}/30`;
            headerText = 'text-white';
            categoryText = config.itemTypes[itemData.type]?.name || itemData.type;
        } else if (isArtifact) {
            cardBg = 'from-amber-800 to-amber-900';
            cardBorder = 'border-amber-600';
            headerBg = 'from-amber-700 to-amber-600';
            headerBorder = 'border-amber-500';
            headerText = 'text-amber-100';
            categoryText = 'Artifact';
        } else {
            debug(CH.ui, itemData.type, config.itemTypes);
            cardBg = 'from-gray-800 to-gray-900';
            cardBorder = 'border-gray-600';
            headerBg = getTierHeaderColor(itemData?.rarity || 'tier1');
            headerBorder = 'border-gray-500';
            headerText = 'text-white';
            categoryText = config.itemTypes[itemData.type].name;
        }

        // Item-specific data
        const itemName = (isArtifact ? itemData.name :
                        (isCrafting ? itemData.name :
                        itemData.name.replace(' Blueprint', '')));
        const itemIcon = (isArtifact ? (itemData.icon || '⚡') :
                        (isCrafting ? (itemData.icon || '⚡') : itemData.icon));
        let description = (!isArtifact ? (itemData.short ? itemData.short : itemData.name) : '') + (isSkillGear || isArtifact ? ' <span class="font-bold">'+getItemEffectDescription(itemData.id)+'</span>' : '');
        let techniqueName = '';
        let techniqueIcon = '';

        if (isSkillGear) {
            const skillName = getSkillDisplayName(getTechniqueSkill(itemData.techniqueId));
            techniqueName = getTechniqueName(itemData.techniqueId);
            techniqueIcon = getTechniqueIcon(itemData.techniqueId);
            description = `<p class='text-blue-500 font-bold'>${skillName}</p><p class='text-green-500 font-bold'>${techniqueName}</p>${description}`;
        }
        else if (isArtifact && itemData.skill) {
            const skillName = getSkillDisplayName(itemData.skill);
            description = `<p class='text-blue-500 font-bold'>${skillName}</p>${description}`;
        }

        const quantity = isArtifact || isSkillGear ? 1 : (isCrafting ? null : item.quantity);

        /**
         * What ONE of these is worth, in credits, opposite the count.
         *
         * The count answers "how many"; on the Syndicate's Stores and the
         * Command Vault that is only half the question, because Operations
         * price a manifest in CREDITS of materials (`cargoValue`) — so a dock
         * of 500 commons and one of 20 rares are worth wildly different things
         * and the count alone cannot say which fills a convoy.
         *
         * PER UNIT, not the pile's total: the pile total is just count × this,
         * and the per-unit figure is the one that survives the count changing.
         * Materials only — artifacts, gear and relics are not on the crafting
         * ladder `materialValue` derives from, and would read as 0.
         *
         * Rendered into the slot the action button leaves empty when actions
         * are hidden, so read-only screens gain it and the sell/craft flows are
         * untouched rather than gaining a third control to line up.
         */
        const unitValue = itemData.type === 'material' ? materialValue(itemData.id) : 0;
        const unitValueBadge = unitValue > 0
            ? `<div class="bg-gray-700 text-gray-100 px-2 py-1 rounded-full text-xs font-bold border border-gray-500 inline-block"
                    title="${unitValue.toLocaleString()}💰 each${quantity != null ? ` — ${(unitValue * quantity).toLocaleString()}💰 for all ${quantity}` : ''}">${unitValue.toLocaleString()}💰</div>`
            : '';

        // Faction icon for materials
        const factionKey = !isArtifact && itemData.type === 'material' ? item.faction : null;
        const factionIcon = factionKey ? getFactionIcon(factionKey) : (itemData.type === 'relic' ? config.itemTypes.relic.icon : '');
        const factionName = factionKey ? getFactionName(factionKey) : '';

        // Action buttons - check if character is on marketplace (read-only mode)
        const isReadOnly = (gameState.character && gameState.character.isMarketplace) || options.hideActions;
        let actionButton = '';
        let qtyLabel = '';

        // Equip state (any mode): drives the equipped-card highlight and the
        // gear-tab equip row. Equipping is non-destructive, so "owned" means
        // in inventory (gear) / in the crafted-artifacts list (artifacts).
        const isEquippedItem =
            (isSkillGear && Object.values(gameState.character?.equippedTechniqueGear || {}).some(g => g && (g.id === itemData.id || g === itemData.id))) ||
            (isArtifact && Object.values(gameState.character?.equippedArtifacts || {}).some(a => a && a.id === itemData.id));

        // Durable gear/artifacts are crafted once per character (see
        // progressionScaling.allowDuplicateCrafts). Owned => the craft control
        // becomes a "✓ CRAFTED" marker instead of an active button.
        const ownsThisItem = isSkillGear
            ? ownsTechGear(gameState.character, itemData.id)
            : isArtifact
                ? ownsArtifact(gameState.character, itemData.id)
                : false;
        const alreadyCrafted = (isSkillGear || isArtifact) && ownsThisItem && !duplicateCraftsAllowed();

        // Crafting-mode (Gear tab) equip row: owned gear/artifacts get an
        // EQUIP/UNEQUIP control that stays visible in the collapsed view too
        // (it sits outside .card-body, which the collapse hides)
        // A crafting-mode artifact whose design isn't unlocked yet: it can't be
        // crafted or expanded - only unlocked. The unlock control takes the
        // collapsed-visible equip-row slot (same place EQUIP sits).
        const artifactLocked = isCrafting && !isReadOnly && isArtifact && !isArtifactUnlocked(itemData.id);

        let equipRow = '';
        if (artifactLocked) {
            const blueprintId = `${itemData.id}-blueprint`;
            const hasBlueprint = (gameState.character?.inventory?.[blueprintId] || 0) > 0;
            const unlockMarks = config.progressionScaling?.commandMarks?.perArtifactUnlock || 0;
            const btn = hasBlueprint
                ? `<button class="bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-400 hover:to-violet-500 text-white px-2 py-0.5 rounded text-xs font-bold border border-violet-400 transition-colors"
                    title="Consume this design's blueprint to unlock it for your account (+${unlockMarks} Command Marks). Any character can then craft it from materials."
                    onclick="unlockArtifact('${itemData.id}'); showCraftingModal();">🔓 UNLOCK +${unlockMarks}🎖️</button>`
                : `<button class="bg-gray-700 text-gray-300 px-2 py-0.5 rounded text-xs font-bold border border-gray-600 cursor-not-allowed" disabled title="This design's blueprint drops from Tier 3 raids">🔒 NEEDS BLUEPRINT</button>`;
            equipRow = `<div class="card-equip-row mx-1 flex justify-end">${btn}</div>`;
        } else if (isCrafting && !isReadOnly && !isConsumable && (isSkillGear || isArtifact)) {
            if (ownsThisItem) {
                let equipButton;
                if (isEquippedItem) {
                    const unequipCall = isArtifact ? `unequipArtifact('${itemData.slot}')` : `unequipTechniqueGear('${itemData.techniqueId}')`;
                    equipButton = `<button class="bg-red-600 hover:bg-red-700 text-white px-2 py-0.5 rounded text-xs font-bold border border-red-500 transition-colors" onclick="${unequipCall}">UNEQUIP</button>`;
                } else {
                    const canEquip = isArtifact
                        ? (canEquipArtifact ? canEquipArtifact(itemData) : true)
                        : (canEquipTechniqueGear ? canEquipTechniqueGear(itemData.id) : true);
                    const requirement = isArtifact
                        ? (getArtifactSkillRequirementMessage ? getArtifactSkillRequirementMessage(itemData) : '')
                        : (getTechniqueGearRequirementMessage ? getTechniqueGearRequirementMessage(itemData.id) : '');
                    const equipCall = isArtifact ? `equipArtifact('${itemData.id}')` : `equipTechniqueGear('${itemData.techniqueId}', '${itemData.id}')`;
                    equipButton = canEquip
                        ? `<button class="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 px-2 py-0.5 rounded text-xs font-bold border border-amber-400 transition-colors" title="${isSkillGear ? 'One gear piece per skill - equipping swaps out the current one' : ''}" onclick="${equipCall}">EQUIP</button>`
                        : `<button class="bg-gray-500 text-gray-300 px-2 py-0.5 rounded text-xs font-bold border border-gray-400 cursor-not-allowed" disabled title="${requirement}">EQUIP</button>`;
                }
                equipRow = `<div class="card-equip-row mx-1 flex justify-end">${equipButton}</div>`;
            }
        }
        
        // Locked artifacts show only the unlock control (in equipRow above);
        // the craft controls appear once the design is unlocked.
        if (isCrafting && !isReadOnly && !artifactLocked && alreadyCrafted) {
            // One-per-character durable already in hand: the craft slot becomes a
            // completed-checklist marker (equip it from the row above).
            actionButton = `<button class="w-full h-10 bg-gray-700 text-emerald-300 rounded text-xs font-bold border border-emerald-600/40 cursor-not-allowed" disabled title="Already crafted - one per character. Equip it from your inventory.">✓ CRAFTED</button>`;
        } else if (isCrafting && !isReadOnly && !artifactLocked) {
            // Crafting mode - show craft button with quantity selector
            const canCraft = recipe ? canCraftRecipe(recipe) : false;
            // Same scaled price calculateBulkCraftingCost charges (parity with the sim)
            const craftingCost = calculateBulkCraftingCost(itemData.id, 1);
            const playerCredits = gameState.player?.credits || 0;
            const hasCredits = playerCredits >= craftingCost;
            const quantityId = `craft-qty-${itemData.id}`;

            if (canCraft && hasCredits) {
                // Calculate cost for selected quantity and show discount
                // Calculate max craftable quantity
                let maxCraftQty = 1;
                if (recipe && recipe.inputs) {
                    maxCraftQty = 99;
                    for (const input of recipe.inputs) {
                        const available = gameState.character.inventory[input.id] || 0;
                        const canCraftQty = Math.floor(available / input.qty);
                        maxCraftQty = Math.min(maxCraftQty, canCraftQty);
                    }
                    maxCraftQty = Math.max(1, maxCraftQty);
                }

                const actionButtonClass = 'flex-grow px-2 h-10 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 rounded text-xs font-bold border border-amber-400 transition-colors';
                const actionButtonCountClass = 'h-10 w-10 bg-gray-600 border border-gray-800 hover:bg-gray-500 text-white rounded text-xs font-bold'
                actionButton = `
                    <div class="w-full flex gap-1">
                        <button class="${actionButtonCountClass}" onclick="decrementCraftQty('${quantityId}')">−</button>
                        <button class="${actionButtonCountClass}" onclick="incrementCraftQty('${quantityId}')">+</button>
                        <input type="number" id="${quantityId}" class="hidden" value="1" min="1" max="${maxCraftQty}" onchange="updateCraftCost('${itemData.id}', '${quantityId}')">                            
                        <button id="craft-btn-${itemData.id}" class="${actionButtonClass}" onclick="const qty = parseInt(document.getElementById('${quantityId}').value || 1); craftItem('${itemData.id}', ${isArtifact}, qty); showCraftingModal();">CRAFT 1<br/>💰${craftingCost}</button>
                    </div>
                `;
            } else {
                // Determine the blocking gate. The artifact blueprint is a
                // regular recipe input (drop-only), so when it is the ONLY
                // shortfall the message says so instead of pointing at the
                // workbench. Otherwise missing materials come first: when
                // the shortfall is craftable, the button becomes an ACTIVE
                // "craft components" jump into the tree-filtered modal
                // instead of a dead end.
                const missingInputs = (recipe?.inputs || []).filter(input =>
                    (gameState.character.inventory[input.id] || 0) < input.qty);
                const materialsMissing = missingInputs.length > 0;
                const blueprintMissing = missingInputs.some(input => input.id.endsWith('-blueprint'));
                const onlyBlueprintMissing = blueprintMissing && missingInputs.every(input => input.id.endsWith('-blueprint'));

                let disabledMessage = null;
                if (onlyBlueprintMissing) {
                    disabledMessage = 'MISSING<br>BLUEPRINT';
                } else if (materialsMissing) {
                    // Craftable components in the chain? Offer the workbench —
                    // but NOT inside the crafting modal itself (the jump would
                    // just reopen the same modal); there it stays disabled.
                    // Raw-only shortfalls stay disabled - those drop in raids.
                    if (!options.inModal && getCraftingTree(itemData.id).length > 1) {
                        actionButton = `<button class="w-full h-10 bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-600 hover:to-blue-700 text-white rounded text-xs font-bold border border-game-accent transition-colors leading-tight"
                            title="Open the crafting tree for this item${blueprintMissing ? ' (blueprint still needed - legendary drop)' : ''}"
                            onclick="showCraftingTreeModal('${itemData.id}')">🔧 CRAFT<br>COMPONENTS</button>`;
                    } else {
                        disabledMessage = 'MISSING<br>MATERIALS';
                    }
                } else if (!hasCredits) {
                    disabledMessage = 'NOT ENOUGH<br>CREDITS';
                } else {
                    disabledMessage = 'MISSING<br>MATERIALS';
                }

                if (disabledMessage) {
                    actionButton = `<button class="w-full h-10 bg-gray-800 text-gray-400 rounded text-xs font-bold border border-gray-600 cursor-not-allowed flex items-center justify-center leading-tight" disabled ${disabledMessage.includes('MATERIALS') ? 'title="Missing materials drop in raids"' : ''}>${disabledMessage}</button>`;
                }
            }
        } else if (isArtifact && !isReadOnly) {
            const isEquipped = Object.values(gameState.character.equippedArtifacts).some(equipped =>
                equipped && equipped.id === itemData.id
            );
            const canEquip = canEquipArtifact ? canEquipArtifact(itemData) : true;
            const skillMessage = getArtifactSkillRequirementMessage ? getArtifactSkillRequirementMessage(itemData) : '';

            if (isEquipped) {
                actionButton = `<button class="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs font-bold border border-red-500 transition-colors" onclick="unequipArtifact('${itemData.slot}')">UNEQUIP</button>`;
            } else if (canEquip) {
                actionButton = `<button class="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 px-2 py-1 rounded text-xs font-bold border border-amber-400 transition-colors" onclick="equipArtifact('${itemData.id}')">EQUIP</button>`;
            } else {
                actionButton = `<button class="bg-gray-500 text-gray-300 px-2 py-1 rounded text-xs font-bold border border-gray-400 cursor-not-allowed" disabled title="${skillMessage}">EQUIP</button>`;
            }
        } else if (isSkillGear && !isReadOnly) {
            const isEquipped = Object.values(gameState.character.equippedTechniqueGear).some(equipped =>
                equipped && equipped.id === itemData.id
            );
            const canEquip = canEquipTechniqueGear ? canEquipTechniqueGear(itemData.id) : true;
            const skillMessage = getTechniqueGearRequirementMessage ? getTechniqueGearRequirementMessage(itemData.id) : '';

            if (isEquipped) {
                actionButton = `<button class="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs font-bold border border-red-500 transition-colors" onclick="unequipTechniqueGear('${itemData.techniqueId}')">UNEQUIP</button>`;
            } else if (canEquip) {
                actionButton = `<button class="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 px-2 py-1 rounded text-xs font-bold border border-amber-400 transition-colors" onclick="equipTechniqueGear('${itemData.techniqueId}', '${itemData.id}')">EQUIP</button>`;
            } else {
                actionButton = `<button class="bg-gray-500 text-gray-300 px-2 py-1 rounded text-xs font-bold border border-gray-400 cursor-not-allowed" disabled title="${skillMessage}">EQUIP</button>`;
            }
        } else {
            const categoryColor = getItemTypeColor(item.type);
            if (item.type === 'consumable') {
                if (isPurchase && !isReadOnly) {
                    // Purchase mode - show BUY button with credit cost
                    // (energy consumables escalate with today's purchases)
                    const credits = getConsumablePurchasePrice(itemData, gameState.player);
                    const playerCredits = gameState.player?.credits || 0;
                    const canBuy = credits > 0 && playerCredits >= credits;
                    const needsCredits = credits - playerCredits;
                    const escalated = itemData.restoresEnergy && credits > (itemData.credits || 0);
                    const buttonLabel = `BUY 💰${credits}`;
                    const escalationHint = escalated ? ` title="Field logistics surcharge: repeat energy orders cost more today (resets daily)"` : '';

                    if (canBuy) {
                        actionButton = `<button class="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white px-2 py-1 rounded text-xs font-bold border border-green-400 transition-colors"${escalationHint} onclick="purchaseConsumable('${itemData.id}')">${buttonLabel}</button>`;
                    } else {
                        actionButton = `<button class="bg-gray-500 text-gray-300 px-2 py-1 rounded text-xs font-bold border border-gray-400 cursor-not-allowed" disabled title="Need ${needsCredits} more credits">${buttonLabel}</button>`;
                    }
                } else if (!isPurchase && !isReadOnly) {
                    if (!itemData.restoresHp && !itemData.restoresEnergy) {
                        // Technique consumable - deployed automatically in raids, no manual USE
                        actionButton = `<span class="bg-gray-700 text-gray-300 px-2 py-1 rounded text-xs font-bold border border-gray-600" title="Rigs automatically on its technique's first success in a raid — the rig lasts the whole raid">⚙️ AUTO</span>`;
                    } else {
                        // Inventory mode - show USE button
                        const canUse = canUseConsumable && canUseConsumable(itemData.id);
                        const requirementMessage = getConsumableRequirementMessage ? getConsumableRequirementMessage(itemData.id) : 'Cannot use';

                        if (canUse) {
                            actionButton = `<button class="bg-gradient-to-r ${categoryColor.button} text-white px-2 py-1 rounded text-xs font-bold border border-gray-600 transition-colors" onclick="useConsumable('${itemData.id}')">USE</button>`;
                        } else {
                            actionButton = `<button class="bg-gray-500 text-gray-300 px-2 py-1 rounded text-xs font-bold border border-gray-400 cursor-not-allowed" disabled title="${requirementMessage}">USE</button>`;
                        }
                    }
                }
            } else if (item.type === 'relic' && !isReadOnly) {
                const quantityId = `sell-qty-${itemData.id}`;
                const actionButtonCountClass = 'px-1 py-1 bg-gray-600 border border-gray-800 hover:bg-gray-500 text-white rounded text-xs font-bold'
                actionButton = `
                    <button class="${actionButtonCountClass}" onclick="decrementSellQty('${quantityId}')">−</button>
                    <button class="${actionButtonCountClass}" onclick="incrementSellQty('${quantityId}')">+</button>
                    <input type="number" id="${quantityId}" class="hidden" value="1" min="1" max="${quantity}" onchange="updateSellQty('${itemData.id}', '${quantityId}')">
                    <button id="sell-btn-${itemData.id}" class="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-2 py-1 rounded text-xs font-bold border border-gray-600 transition-colors" onclick="sellItem('${itemData.id}')">SELL 1</button>`;
            }
        }

        // Collapse state + progress (collapsed view shows name, %, and a bar).
        // displayName overrides the shown title; hideToggle drops the expand
        // chevron (both used by the gear tab for un-blueprinted artifacts).
        const { collapsed = false, progress = null, displayName = null, hideToggle = false, progressQ = null } = options || {};
        // A locked artifact can't be expanded (nothing to act on but the unlock
        // control, which shows in the collapsed view) - force collapsed + no toggle
        const effCollapsed = collapsed || artifactLocked;
        const effHideToggle = hideToggle || artifactLocked;
        let progressValue = progress;
        let progressQty = progressQ;

        if ((progressValue == null || progressQty == null) && isCrafting && recipe && recipe.inputs) {
            let haveSum = 0, needSum = 0;
            recipe.inputs.forEach(input => {
                const have = gameState.character?.inventory?.[input.id] || 0;
                haveSum += Math.min(have, input.qty);
                needSum += input.qty;
            });
            progressValue = needSum > 0 ? haveSum / needSum : 0;
            progressQty = gameState.character?.inventory?.[recipe.target] || 0;
        }
        const progressPct = progressValue != null ? Math.round(progressValue * 100) : null;

        return `
            <div class="inv-card ${effCollapsed ? 'inv-card-collapsed' : ''} ${isEquippedItem ? 'inv-card-equipped' : ''} relative bg-gradient-to-b ${cardBg} border-4 ${cardBorder} rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 ${isCrafting ? '' : 'h-[17rem]'} ${isCrafting ? 'w-52' : 'w-44'} overflow-hidden ${isArtifact ? 'foil-card foil-card-bg' : ''}" data-item-id="${itemData.id}">
                <!-- Card Border Frame -->
                <div class="absolute"></div>

                <!-- Header Banner -->
                <div class="relative bg-gradient-to-r ${headerBg} ${headerText} px-3 py-2 text-left border-b-2 ${headerBorder} h-8">
                    <div class="text-xs font-bold uppercase tracking-wide">${categoryText}</div>
                    ${options.isFocus ? '<div class="absolute top-[0.2rem] right-[5rem] bg-black bg-opacity-30 border border-black rounded-full h-6 px-1 flex items-center justify-center text-sm" title="Suggested focus">🎯</div>' : ''}
                    ${factionIcon ? `<div class="inv-card-faction-icon absolute top-[0.2rem] right-[3.75rem] bg-black bg-opacity-30 border border-black rounded-full w-6 h-6 flex items-center justify-center text-sm" title="${factionName}">${factionIcon}</div>` : ''}
                    ${itemIcon ? `<div class="inv-card-item-icon absolute top-[0.2rem] right-[1.75rem] bg-black bg-opacity-30 border border-black rounded-full h-6 px-1 flex items-center justify-center text-sm" title="${isSkillGear ? 'Technique: '+techniqueName : ''}">${isSkillGear ? techniqueIcon : itemIcon}</div>` : ''}
                    <!-- Expand/collapse toggle (top-right corner) -->
                    ${effHideToggle ? '' : `<div class="absolute top-[0.2rem] right-[0.2rem] z-20 bg-black bg-opacity-40 border border-gray-500 rounded-full w-6 h-6 flex items-center justify-center text-xs cursor-pointer hover:border-blue-400 hover:text-blue-300"
                         onclick="event.stopPropagation(); toggleInventoryCardCollapse(this);" title="Expand / collapse">
                        <span class="inv-toggle-open">▸</span><span class="inv-toggle-close">▾</span>
                    </div>`}
                </div>

                <!-- Card Content -->
                <div class="relative px-2 pb-2">
                    <!-- Title Section -->
                    <div class="min-h-8 flex items-center mx-1 my-2">
                        <h4 class="font-bold text-white text-xs leading-tight flex-grow" ${effCollapsed ? "title='"+itemData.short+"'" : ""}>${displayName || itemName}</h4>
                        ${progressPct != null ? `<span class="card-progress-pct ml-2 text-[12px] font-bold bg-gray-800 px-2 py-1 rounded-full border border-gray-500 ${progressPct == 100 || progressQty > 0 ? 'text-green-400' : 'text-blue-300'}" title="${progressQty > 0 ? "Item qty in inventory" : "Recipe components in inventory"}">${progressPct < 100 && progressQty == 0 ? progressPct+"%" : "x" + progressQty}</span>` : ''}
                    </div>
                    ${progressPct != null ? `
                    <!-- Collapsed-view progress bar -->
                    <div class="card-progress-row mx-1 mb-2">
                        <div class="h-1.5 rounded bg-gray-800 overflow-hidden border border-gray-600">
                            <div class="h-full ${progressPct >= 100 ? 'bg-green-500' : (progressPct >= 99 ? 'bg-sky-300' : 'bg-blue-500')}" style="width:${Math.min(100, progressPct)}%"></div>
                        </div>
                    </div>` : ''}
                    ${equipRow}
                    ${quantity != null && !isCrafting ? `
                    <div class="card-collapsed-qty flex justify-between items-center mb-2">
                        <div class="bg-gray-700 text-gray-100 px-2 py-1 rounded-full text-xs font-bold border border-gray-500 inline-block">×${quantity}</div>
                        ${actionButton || unitValueBadge}
                    </div>` : ''}

                    <div class="card-body">
                    <!-- Artwork Area with Tippy Tooltip -->
                    <div class="inventory-tooltip relative bg-gradient-to-b from-gray-700 to-gray-800 border-2 border-gray-500 rounded-lg mt-2 mb-2 ${isCrafting ? 'h-24' : 'h-36'} w-full overflow-hidden cursor-pointer">
                        <!-- Hidden tooltip content -->
                        <div class="inventory-tooltip-content hidden">${description}</div>

                        <!-- Icon/Artwork -->
                        <div class="absolute inset-0 flex items-center justify-center">
                            <span class="text-4xl opacity-80 hover:opacity-60 transition-opacity duration-200">${itemIcon}</span>
                        </div>

                        <!-- Info Icon (top-right corner) to indicate tooltip -->
                        <div class="absolute top-1 right-1 z-10 bg-black bg-opacity-50 text-gray-300 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold border border-gray-500 hover:text-blue-300 hover:border-blue-400 transition-colors">ℹ</div>

                        <!-- Credit Value Badge (bottom-right corner of image) for relics -->
                        ${itemData.type === 'relic' && itemData && itemData.credits ?
                            `<div class="absolute bottom-1 right-1 z-5 bg-gray-800 text-amber-200 px-2 py-1 rounded text-xs font-bold border border-gray-600 shadow-lg">💰${itemData.credits}</div>` : ''
                        }
                    </div>

                    ${isCrafting ?
                        // Crafting mode: Materials at top, button at bottom
                        `<!-- Material Requirements -->
                        <div class="mb-2 text-xs bg-gradient-to-b from-gray-800 to-gray-900 border-2 border-gray-500 rounded-lg p-2 grid grid-cols-2 grid-rows-4 gap-x-2 gap-y-0.5" style="height: 140px; overflow: hidden; grid-auto-flow: column;">
                            ${(() => {
                                let requirements = '';
                                // The artifact blueprint is a regular recipe
                                // input now, so it renders in the generic
                                // rows below like any other requirement

                                if (!recipe) {
                                    requirements += '<div class="text-red-400">Recipe not found</div>';
                                } else {
                                    // Use pre-computed materials if available, otherwise fetch on-demand
                                    const materialIds = recipe.inputs.map(input => {return input.id;});
                                    const materialItems = preComputedMaterials || findItemsById(materialIds);
                                    requirements += recipe.inputs.map(input => {
                                        const materialId = input.id;
                                        const required = input.qty;
                                        const available = gameState.character.inventory[materialId] || 0;
                                        const hasEnough = available >= required;
                                        const material = materialItems[materialId];
                                        return `<div class="flex justify-between items-center">
                                            <span class="flex items-center cursor-pointer" title="${material?.name}">
                                                <span class="text-base mr-1 ${hasEnough ? 'text-blue-300' : 'text-amber-300'}">${material?.icon}</span>
                                            </span>
                                            <span class="flex-shrink-0 ${hasEnough ? 'text-blue-300' : 'text-amber-300'}">${available}/${required}</span>
                                        </div>`;
                                    }).join('');
                                }

                                return requirements;
                            })()}
                        </div>
                        <!-- Action Button at Bottom -->
                        <div class="">
                            ${actionButton}
                        </div>` : 

                        isPurchase ?
                            // Consumable purchase modal
                            `<!-- Action Button Section with Quantity -->
                            <div class="flex flex-row-reverse">
                                ${actionButton}
                            </div>` : 
                            // Regular inventory mode: quantity and button side by side
                            `<!-- Action Button Section with Quantity -->
                            <div class="flex justify-between items-center">
                                <div class="bg-gray-700 text-gray-100 px-2 py-1 rounded-full text-xs font-bold border border-gray-500">×${quantity}</div>
                                ${actionButton}
                            </div>`
                    }
                    </div>
                </div>
            </div>
        `;
                }
}

/**
 * Toggle a card between its full view and the collapsed view (name +
 * progress only). DOM-state toggle - pages that re-render (Gear tab) patch
 * this onclick to their own persistent handler.
 */
export function toggleInventoryCardCollapse(el) {
    el.closest('.inv-card')?.classList.toggle('inv-card-collapsed');
}
if (typeof window !== 'undefined') {
    window.toggleInventoryCardCollapse = toggleInventoryCardCollapse;
}

// Helper export function to check if a consumable can be used
// During an active raid the team can only use HP consumables while resting at a campsite room
export function isAtCampsite() {
    const raid = gameState.currentRaid?.raidInProgress;
    if (!raid) return false;
    const step = gameState.currentRaid.selectedPath?.[raid.currentRoomIndex];
    return step?.room?.type === 'campsite';
}

export function canUseConsumable(itemId) {
    if (!gameState.character) return false;
    if (!gameState.character.inventory[itemId] || gameState.character.inventory[itemId] <= 0) return false;
    // Ration Cut. Here rather than in useConsumable() so the button renders
    // disabled with a reason instead of failing on click — canUseConsumable is
    // what the card reads to decide.
    if (blackoutModifier('noConsumables', false)) return false;

    // Same classification as useConsumable: the item's restore fields decide;
    // technique consumables (neither field) auto-deploy in raids, never manual
    const itemData = findItem('id', itemId);
    if (itemData?.restoresEnergy) {
        // Energy consumables are blocked entirely once a raid is active (planning or in progress)
        const isRaidActive = gameState.currentRaid && (gameState.currentRaid.raidInProgress || gameState.currentRaid.currentRaidGrid);
        if (isRaidActive) return false;
        return gameState.player.energy < gameState.player.maxEnergy;
    }
    if (!itemData?.restoresHp) return false;
    if (gameState.currentRaid?.raidInProgress && !isAtCampsite()) return false;
    return gameState.character.currentHp < gameState.character.maxHp;
}

// Helper export function to get usage requirement message
export function getConsumableRequirementMessage(itemId) {
    if (blackoutModifier('noConsumables', false)) return 'Ration Cut: no consumables on this contract';
    const itemData = findItem('id', itemId);
    if (itemData?.restoresEnergy) {
        const isRaidActive = gameState.currentRaid && (gameState.currentRaid.raidInProgress || gameState.currentRaid.currentRaidGrid);
        if (isRaidActive) return 'Only usable before starting a raid';
        return 'Energy already full';
    }
    if (!itemData?.restoresHp) return 'Rigs automatically on its technique\'s first success in a raid';
    if (gameState.currentRaid?.raidInProgress && !isAtCampsite()) return 'Only usable at a campsite during a raid';
    return 'Health already full';
}

// Test export function to generate random inventory items
export function generateTestInventory() {
    // Ensure we have an active character
    if (!gameState.character || !gameState.activeCharacterId) {
        addNotification('No active character! Please create or select a character first.', 'error');
        return;
    }

    // Clear existing inventory and artifacts for current character
    gameState.character.inventory = {};
    gameState.character.artifacts = [];

    // Ensure we get items from each rarity tier
    const rarityTiers = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'unique'];
    rarityTiers.forEach(rarity => {
        const tierItems = getAllItems().filter(item => {
            return item.rarity === rarity;
        });

        if (tierItems.length > 0) {
            // Add a number of item ids depending on the tier
            const count = Math.min(1 + Math.floor(Math.random() * 3), tierItems.length) * (6-rarityTiers.indexOf(rarity));
            const shuffled = [...tierItems].sort(() => Math.random() - 0.5);

            for (let i = 0; i < count; i++) {
                const randomItem = shuffled[i];
                debug(CH.ui, "adding random item:", randomItem);
                let quantity;
                if (randomItem.type === 'storyRelic' || randomItem.type === 'blueprint' || randomItem.type === 'techGear') {
                    quantity = 1;
                } else if (randomItem.type === 'relic') {
                    quantity = 3 + Math.floor(Math.random() * 3);
                } else if (randomItem.type === 'consumable') {
                    quantity = 5 + Math.floor(Math.random() * 3);
                } else {
                    quantity = 1000;
                }
                gameState.character.inventory[randomItem.id] = (gameState.character.inventory[randomItem.id] || 0) + quantity;
            }
        }
    });

    // Generate 3-5 predefined skill artifacts from artifactData
    const numArtifacts = 3 + Math.floor(Math.random() * 3);
    let bAddedUtilArtifact = false, i = 0;
    while(i < numArtifacts) {
        // Select a random artifact from the predefined list
        const allArtifacts = getAllArtifacts();
        const randomArtifact = allArtifacts[Math.floor(Math.random() * allArtifacts.length)];
        
        if (i > 1 && !bAddedUtilArtifact && randomArtifact.slot == 'skillSlot')
            continue;
        
        if (randomArtifact.slot != 'skillSlot')
            bAddedUtilArtifact = true;

        // Create artifact instance with unique ID (matching crafting system format)
        const artifact = {
            key: Date.now() + Math.random() + i, // Unique ID matching crafting system
            id: randomArtifact.id,
            name: randomArtifact.name,
            type: randomArtifact.type,
            slot: randomArtifact.slot,
            skill: randomArtifact.skill,
            effects: randomArtifact.effects, // Direct reference like crafting system
            description: randomArtifact.description,
            short: randomArtifact.short,
            faction: randomArtifact.faction,
            rarity: randomArtifact.rarity,
            icon: randomArtifact.icon, // Include the icon from artifactData
            crafted: true // Mark as crafted like the crafting system
        };

        // Only add if not already in artifacts array
        if (!gameState.character.artifacts.some(a => a.id === artifact.id)) {
            gameState.character.artifacts.push(artifact);
        }
        i++;
    }

    // Save current character's inventory and artifacts to their character data
    const currentCharIndex = gameState.characters.findIndex(c => c.id === gameState.activeCharacterId);
    if (currentCharIndex !== -1) {
        gameState.characters[currentCharIndex].inventory = { ...gameState.character.inventory};
        gameState.characters[currentCharIndex].artifacts = [...gameState.character.artifacts];
    }

    // Refresh Alpine store
    refreshAlpineInventory();

    // Show notification
    addNotification(`Generated random items!`, 'success');

    // Update UI and save
    updateUI();
    saveGameState();
}

// Clear inventory export function for testing
export function clearInventory() {
    // Ensure we have an active character
    if (!gameState.character || !gameState.activeCharacterId) {
        addNotification('No active character! Please create or select a character first.', 'error');
        return;
    }

    // Clear current character's inventory and artifacts
    gameState.character.inventory = {};
    gameState.character.artifacts = [];
    gameState.character.equippedArtifacts = {
        skillSlot: null,
        utilitySlot: null
    };

    // Save current character's cleared inventory to their character data
    const currentCharIndex = gameState.characters.findIndex(c => c.id === gameState.activeCharacterId);
    if (currentCharIndex !== -1) {
        gameState.characters[currentCharIndex].inventory = {};
        gameState.characters[currentCharIndex].artifacts = [];
        gameState.characters[currentCharIndex].equippedArtifacts = {
            skillSlot: null,
            utilitySlot: null
        };
    }

    // Refresh Alpine store
    refreshAlpineInventory();

    // Show notification
    addNotification('Inventory cleared!', 'success');

    // Update UI and save
    updateUI();
    saveGameState();
}

// Update inventory header with current character name
export function updateInventoryCharacterHeader() {
    const inventoryCharacterName = document.getElementById('inventory-character-name');
    if (inventoryCharacterName && gameState.character) {
        inventoryCharacterName.textContent = gameState.character.name + "'s";
    }
}

export function getItemEffectDescription(itemId) {
    const item = findItem('id', itemId) || findArtifact('id', itemId);
    if (!item || !item.type || !item.effects) {
        return '';
    }

    return Object.entries(item.effects || {}).map(([effectId, amt]) => {
        const effect = getEffect(effectId);
        let effectQual = '';
        if (effect.unit == 'percent')
            effectQual += ` by ${amt}%`;
        else if (effect.unit == 'count')
            effectQual += ` ${amt} time${amt>1?'s':''}`;
        return effect.description + effectQual + '.';
    }).join('  ');
}

// Crafting Quantity Controls
/**
 * Calculate maximum quantity that can be crafted based on available materials
 * @param {string} itemId - The item to craft
 * @returns {number} - Maximum craftable quantity
 */
export function getMaxCraftableQty(itemId) {
    if (!gameState.character) return 0;

    const recipe = getRecipeById ? getRecipeById(itemId) : null;
    if (!recipe || !recipe.inputs) return 0;

    // Find the limiting factor (material with lowest quantity available)
    let maxQty = 99; // Start with max possible
    for (const input of recipe.inputs) {
        const available = gameState.character.inventory[input.id] || 0;
        const canCraftQty = Math.floor(available / input.qty);
        maxQty = Math.min(maxQty, canCraftQty);
    }

    return Math.max(1, maxQty); // Always allow at least 1
}

export function incrementSellQty(quantityId) {
    const input = document.getElementById(quantityId);
    if (input) {
        const itemId = quantityId.replace('sell-qty-', '');
        const currentVal = parseInt(input.value) || 1;
        const maxQty = gameState.character.inventory[itemId];
        const newVal = Math.min(currentVal + 1, maxQty);
        input.value = newVal;
        updateSellQty(itemId, quantityId);
    }
}

export function decrementSellQty(quantityId) {
    const input = document.getElementById(quantityId);
    if (input) {
        const itemId = quantityId.replace('sell-qty-', '');
        const currentVal = parseInt(input.value) || 1;
        const newVal = Math.max(currentVal - 1, 1);
        input.value = newVal;
        updateSellQty(itemId, quantityId);
    }
}

export function updateSellQty(itemId, quantityId) {
    const input = document.getElementById(quantityId);
    const sellBtn = document.getElementById(`sell-btn-${itemId}`);
    
    if (input && sellBtn) {
        const quantity = parseInt(input.value) || 1;
        const maxQty = gameState.character.inventory[itemId];

        // Clamp to max craftable
        if (quantity > maxQty) {
            input.value = maxQty;
        }

        sellBtn.innerHTML = `SELL ${quantity}`;
    }
}

export function incrementCraftQty(quantityId) {
    const input = document.getElementById(quantityId);
    if (input) {
        const itemId = quantityId.replace('craft-qty-', '');
        const currentVal = parseInt(input.value) || 1;
        const maxQty = getMaxCraftableQty(itemId);
        const newVal = Math.min(currentVal + 1, maxQty);
        input.value = newVal;
        updateCraftCost(itemId, quantityId);
    }
}

export function decrementCraftQty(quantityId) {
    const input = document.getElementById(quantityId);
    if (input) {
        const itemId = quantityId.replace('craft-qty-', '');
        const currentVal = parseInt(input.value) || 1;
        const newVal = Math.max(currentVal - 1, 1);
        input.value = newVal;
        updateCraftCost(itemId, quantityId);
    }
}

export function updateCraftCost(itemId, quantityId) {
    const input = document.getElementById(quantityId);
    const craftBtn = document.getElementById(`craft-btn-${itemId}`);
    
    if (input && craftBtn && window.calculateBulkCraftingCost) {
        const quantity = parseInt(input.value) || 1;
        const maxQty = getMaxCraftableQty(itemId);

        // Clamp to max craftable
        if (quantity > maxQty) {
            input.value = maxQty;
        }

        const totalCost = calculateBulkCraftingCost(itemId, quantity);
        craftBtn.innerHTML = `CRAFT ${quantity}<br/>💰${totalCost}`;
    }
}

// Purchase Modal Functions

// Render (or re-render) the purchase grid. Called on open AND after every
// purchase - energy consumables have same-day escalating prices, so the BUY
// labels must reflect the CURRENT price, not the price at modal-open time.
export function renderPurchaseModalGrid() {
    // HP consumables only - energy is bought (and applied) one-step via the
    // header's ⚡ popup, so a second buy-then-use path here would just confuse
    const purchasableConsumables = [
        'field-health-stim',
        'recovery-injector-mk2',
        'advanced-recovery-serum'
    ];

    const grid = document.getElementById('purchasable-items-grid');
    const noItems = document.getElementById('no-purchasable-items');

    if (!grid || !noItems) return;

    // Clear grid
    grid.innerHTML = '';

    // Add each purchasable item to the grid
    let hasItems = false;
    purchasableConsumables.forEach(itemId => {
        const item = findItem('id', itemId);
        if (item) {
            hasItems = true;
            // Create a modified item object for display in purchase mode
            const displayItem = {
                ...item,
                id: itemId,
                quantity: 0,
                type: 'consumable'
            };

            // Create card using the existing createInventoryCard with 'purchase' mode
            const cardHtml = createInventoryCard(displayItem, 'purchase');
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = cardHtml;
            grid.appendChild(tempDiv.firstElementChild);
        }
    });

    // Show/hide no items message
    noItems.classList.toggle('hidden', hasItems);
}

export function openPurchaseModal() {
    renderPurchaseModalGrid();

    // Show modal
    const modal = document.getElementById('purchase-consumables-modal');
    if (modal) {
        modal.classList.remove('hidden');
    }

    // Reinitialize tooltips for the newly created cards
    if (window.tippy) {
        tippy(".inventory-tooltip", {
            theme: "dark",
            placement: "top",
            delay: [300, 0],
            maxWidth: 300,
            allowHTML: true,
            content(reference) {
                const descDiv = reference.querySelector(".inventory-tooltip-content");
                return descDiv ? descDiv.innerHTML : "";
            },
            onMount(instance) {
                instance.popper.style.border = "2px solid white";
                instance.popper.style.borderRadius = "0.5rem";
            },
            onShow(instance) {
                // Create a named function so it can be accurately removed later
                instance._scrollHandler = () => instance.hide();
                window.addEventListener('scroll', instance._scrollHandler, { passive: true });
            },
            onHide(instance) {
                // Clean up the listener to prevent memory leaks
                window.removeEventListener('scroll', instance._scrollHandler);
            }
        });
    }
}

export function closePurchaseModal() {
    const modal = document.getElementById('purchase-consumables-modal');
    if (modal) {
        modal.classList.add('hidden');
    }
}

// ---------------------------------------------------------------------------
// Header energy popup - one-step purchase: buying applies the energy
// immediately (no inventory round-trip). Same rules as buy-then-use had:
// escalating same-day prices, blocked while a raid is planned or running.
// ---------------------------------------------------------------------------

const ENERGY_POPUP_ITEMS = ['portable-energy-pack', 'energy-conversion-pack', 'power-cell-mk3'];

function isRaidBlockingEnergy() {
    return !!(gameState.currentRaid && (gameState.currentRaid.raidInProgress || gameState.currentRaid.currentRaidGrid));
}

export function renderEnergyPopup() {
    const popup = document.getElementById('energy-popup');
    if (!popup) return;

    const player = gameState.player || { energy: 0, maxEnergy: 100, credits: 0 };
    const missing = Math.max(0, (player.maxEnergy || 0) - (player.energy || 0));
    const raidBlocked = isRaidBlockingEnergy();
    const anyEscalated = ENERGY_POPUP_ITEMS.some(id => {
        const item = findItem('id', id);
        return item && getConsumablePurchasePrice(item, player) > (item.credits || 0);
    });

    const rows = ENERGY_POPUP_ITEMS.map(id => {
        const item = findItem('id', id);
        if (!item) return '';
        const price = getConsumablePurchasePrice(item, player);
        const gain = Math.min(item.restoresEnergy, missing);
        const gainLabel = gain < item.restoresEnergy ? `+${gain}/${item.restoresEnergy}` : `+${item.restoresEnergy}`;
        const disabled = raidBlocked || missing <= 0 || (player.credits || 0) < price;
        const title = raidBlocked ? 'Not available while a raid is being planned or running'
            : missing <= 0 ? 'Energy already full'
            : (player.credits || 0) < price ? `Need ${price - (player.credits || 0)} more credits`
            : `Restores ${gain} energy instantly`;
        return `
            <button class="w-full flex items-center gap-2 px-2 py-1.5 rounded border text-sm text-left transition-colors
                ${disabled ? 'border-gray-700 text-gray-500 cursor-not-allowed' : 'border-blue-800 text-gray-200 hover:bg-blue-900/40'}"
                ${disabled ? 'disabled' : ''} title="${title}" onclick="buyEnergyDirect('${id}')">
                <span>${item.icon}</span>
                <span class="flex-1">${item.name}</span>
                <span class="text-blue-400 font-bold whitespace-nowrap">${gainLabel} ⚡</span>
                <span class="text-game-gold font-bold whitespace-nowrap">💰${price}</span>
            </button>`;
    }).join('');

    popup.innerHTML = `
        <div class="text-[11px] uppercase tracking-widest text-gray-400 mb-2">Buy Energy — applied instantly</div>
        <div class="flex flex-col gap-1.5">${rows}</div>
        ${raidBlocked ? '<div class="text-[11px] text-red-400 mt-2">Not available while a raid is planned or running</div>' : ''}
        ${anyEscalated ? '<div class="text-[11px] text-gray-500 mt-2">Field logistics surcharge: repeat energy orders cost more today (resets daily)</div>' : ''}`;
}

export function toggleEnergyPopup(event) {
    event?.stopPropagation();
    const popup = document.getElementById('energy-popup');
    if (!popup) return;
    const opening = popup.classList.contains('hidden');
    popup.classList.toggle('hidden', !opening);
    if (opening) {
        // Fixed-position under the header's ⚡ chip. The popup is a top-level
        // element (see index.html) rather than a header child, so it is
        // anchored by id — there is no useful parent to fall back to, and the
        // currency chips it points at are re-rendered from the layer registry.
        const chip = document.getElementById('energy-chip')?.getBoundingClientRect();
        if (chip) {
            popup.style.top = `${chip.bottom + 8}px`;
            popup.style.right = `${Math.max(8, window.innerWidth - chip.right)}px`;
        }
        renderEnergyPopup();
        // Close on any click outside the popup (the opener button stops
        // propagation). A detached target means the click hit a row that
        // buyEnergyDirect just re-rendered away - that's inside, stay open.
        const closeOnOutside = (e) => {
            if (!popup.contains(e.target) && document.contains(e.target)) {
                popup.classList.add('hidden');
                document.removeEventListener('click', closeOnOutside);
            }
        };
        document.addEventListener('click', closeOnOutside);
    }
}

export function buyEnergyDirect(itemId) {
    const itemData = findItem('id', itemId);
    if (!itemData?.restoresEnergy) {
        addNotification('This item cannot be purchased here!', 'error');
        return;
    }
    if (isRaidBlockingEnergy()) {
        addNotification('Energy can only be bought before starting a raid!', 'error');
        return;
    }
    const player = gameState.player;
    if (!player || player.energy >= player.maxEnergy) {
        addNotification('Your energy is already full!', 'info');
        return;
    }
    const price = getConsumablePurchasePrice(itemData, player);
    if (player.credits < price) {
        addNotification(`Not enough credits! Need ${price}, have ${player.credits}`, 'error');
        return;
    }

    player.credits -= price;
    recordEnergyPurchase(player);
    const gained = Math.min(itemData.restoresEnergy, player.maxEnergy - player.energy);
    player.energy += gained;

    addNotification(`${itemData.icon} ${itemData.name}: +${gained} energy for ${price} credits`, 'success');
    renderEnergyPopup(); // prices escalate per purchase - refresh labels
    updateUI();
    saveGameState();
}

// Setup modal close button handler
export function setupPurchaseModalHandler() {
    const closeBtn = document.getElementById('close-purchase-modal');
    if (closeBtn) {
        closeBtn.addEventListener('click', closePurchaseModal);
    }

    // Close modal when clicking outside
    const modal = document.getElementById('purchase-consumables-modal');
    if (modal) {
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                closePurchaseModal();
            }
        });
    }
}